import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import * as eventsService from '../services/eventsService';
import * as registrationsService from '../services/registrationsService';
import * as homeworkService from '../services/homeworkService';
import * as progressService from '../services/progressService';
import * as profileService from '../services/profileService';
import * as orientationRegistrationsService from '../services/orientationRegistrationsService';
import SessionsList from '../components/Student/SessionsList';
import ResourceProgressTracker from '../components/Student/ResourceProgressTracker';
import HomeworkCard from '../components/Student/HomeworkCard';
import SafeIcon from '../common/SafeIcon';
import { getPublicImageUrl } from '../utils/storage';
import { formatEventLocationDisplay } from '../utils/eventLocation';
import * as FiIcons from 'react-icons/fi';
import toast from 'react-hot-toast';

const { FiArrowLeft, FiUser, FiDownload, FiExternalLink } = FiIcons;

export default function EventDetailsStudent() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const studentId = profile?.id ?? null;

  const [data, setData] = useState(null);
  const [registration, setRegistration] = useState(null);
  const [orientationOnly, setOrientationOnly] = useState(false);
  const [orientationLeadId, setOrientationLeadId] = useState(null);
  const [orientationEmail, setOrientationEmail] = useState(null);
  const [agentProfile, setAgentProfile] = useState(null);
  const [homeworkWithSubmissions, setHomeworkWithSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [progressLoading, setProgressLoading] = useState(false);

  useEffect(() => {
    if (!eventId || !studentId) {
      setLoading(false);
      return;
    }

    (async () => {
      setLoading(true);
      const options = { role: 'student', userId: studentId };

      const regList = await registrationsService.listRegistrations({
        ...options,
        filters: [{ column: 'event_id', operator: 'eq', value: eventId }]
      });
      const reg = regList.data?.[0] ?? null;

      if (reg) {
        const res = await eventsService.getEventWithProgress(eventId, studentId, options);
        if (res.error) {
          toast.error(res.error.message || 'Failed to load event');
          setLoading(false);
          return;
        }
        setData(res.data);
        setRegistration(reg);
        setOrientationOnly(false);
        if (reg?.invited_by_agent_id || reg?.primary_agent_id) {
          const agentId = reg.invited_by_agent_id || reg.primary_agent_id;
          const agentRes = await profileService.getAgentProfileById(agentId);
          setAgentProfile(agentRes.data ?? null);
        }
        const homework = res.data?.homework ?? [];
        const withSubs = await Promise.all(
          homework.map(async (hw) => {
            const subRes = await homeworkService.getHomeworkSubmission(hw.id, studentId, options);
            return { ...hw, submission: subRes.data };
          })
        );
        setHomeworkWithSubmissions(withSubs);
      } else {
        const orList = await orientationRegistrationsService.listOrientationRegistrations({
          ...options,
          eventIds: [eventId],
          completedFullRegistration: false
        });
        const orReg = (orList.data ?? []).find((r) => r.event_id === eventId);
        if (orReg && orReg.event?.id) {
          const orientationSessionId = orReg.event.orientation_session_id;
          const publicEvent = await eventsService.getPublicEventById(eventId);
          const eventData = publicEvent.data || orReg.event;
          const sessionsRes = await eventsService.getEventSessions(eventId);
          const allSessions = sessionsRes.data ?? [];
          const sessions = orientationSessionId
            ? allSessions.filter((s) => s.id === orientationSessionId)
            : allSessions.slice(0, 1);
          setData({
            ...eventData,
            sessions,
            instructors: [],
            resources: [],
            learningObjectives: [],
            homework: [],
            resourceProgress: {},
            sessionProgress: {},
            progressPercentage: 0,
            completedResources: 0,
            totalResources: 0
          });
          setRegistration(null);
          setOrientationOnly(true);
          setOrientationLeadId(orReg.lead_id);
          setOrientationEmail(orReg.email ?? '');
          setHomeworkWithSubmissions([]);
        } else {
          const res = await eventsService.getEventWithProgress(eventId, studentId, options);
          if (res.error) {
            toast.error(res.error.message || 'Event not found or you’re not registered.');
            setData(null);
            setLoading(false);
            return;
          }
          setData(res.data);
          setRegistration(null);
          setOrientationOnly(false);
          setHomeworkWithSubmissions([]);
        }
      }

      setLoading(false);
    })();
  }, [eventId, studentId]);

  const handleMarkResourceComplete = async (resourceId, isCompleted) => {
    if (!studentId || !data) return;
    setProgressLoading(true);
    const res = await progressService.markResourceComplete(
      studentId,
      resourceId,
      data.id,
      studentId,
      isCompleted,
      { role: 'student', userId: studentId }
    );
    setProgressLoading(false);
    if (res.error) {
      toast.error(res.error.message || 'Failed to update progress');
      return;
    }
    const nextProgress = { ...(data.resourceProgress || {}), [resourceId]: res.data };
    setData((prev) => ({ ...prev, resourceProgress: nextProgress }));
    const total = data.resources?.length ?? 0;
    const completed = Object.values(nextProgress).filter((p) => p?.is_completed).length;
    setData((prev) => ({
      ...prev,
      resourceProgress: nextProgress,
      progressPercentage: total ? Math.round((completed / total) * 100) : 0,
      completedResources: completed,
      totalResources: total
    }));
    toast.success(isCompleted ? 'Marked complete' : 'Marked incomplete');
  };

  const handleSubmitHomework = async (homeworkId, submissionData) => {
    if (!studentId) return;
    const res = await homeworkService.submitHomework(
      homeworkId,
      studentId,
      submissionData,
      { role: 'student', userId: studentId }
    );
    if (res.error) {
      toast.error(res.error.message || 'Failed to submit');
      return;
    }
    setHomeworkWithSubmissions((prev) =>
      prev.map((hw) => (hw.id === homeworkId ? { ...hw, submission: res.data } : hw))
    );
    toast.success('Submission saved');
  };

  if (loading && !data) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="h-8 w-48 bg-slate-200 rounded animate-pulse" />
        <div className="h-64 bg-slate-100 rounded-xl animate-pulse" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center">
        <p className="text-slate-600">Event not found or you’re not registered.</p>
        <button
          type="button"
          onClick={() => navigate('/dashboard/student-dashboard')}
          className="mt-4 text-[#00AAFF] font-medium hover:underline"
        >
          Back to dashboard
        </button>
      </div>
    );
  }

  const imageUrl = data.featured_image_url || data.image_url;
  const eventLocationText = formatEventLocationDisplay(data);

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900"
      >
        <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
        Back
      </button>

      {/* Event overview */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
        {imageUrl && (
          <div className="aspect-[21/9] bg-slate-100">
            <img src={imageUrl} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="p-6">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-sm font-medium text-slate-500 capitalize">{data.type}</span>
            <span className="text-sm text-slate-400">·</span>
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                data.status === 'public' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'
              }`}
            >
              {data.status}
            </span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">{data.title}</h1>
          {eventLocationText && (
            <p className="text-slate-600 mt-2">{eventLocationText}</p>
          )}
          {data.description && (
            <p className="text-slate-600 mt-2 whitespace-pre-wrap">{data.description}</p>
          )}
          {registration && (
            <p className="text-sm text-slate-500 mt-2">
              Registered · Payment: {registration.payment_status ?? '—'}
            </p>
          )}
          {orientationOnly && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800 font-medium">You’re registered for the free orientation.</p>
              <p className="text-sm text-amber-700 mt-1">Complete your registration to access all sessions and materials.</p>
              <Link
                to={orientationLeadId && orientationEmail ? `/complete-registration/${eventId}?leadId=${orientationLeadId}&email=${encodeURIComponent(orientationEmail)}` : '/dashboard/student-dashboard'}
                className="inline-block mt-2 text-sm font-medium text-amber-800 hover:underline"
              >
                Complete registration →
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Sessions & schedule */}
      <section>
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Sessions & schedule</h2>
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <SessionsList
            sessions={data.sessions ?? []}
            eventZoomLink={data.zoom_meeting_link}
            eventTimezone={data.timezone || 'America/New_York'}
          />
        </div>
      </section>

      {/* Instructors */}
      {data.instructors?.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Instructors</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {data.instructors.map((inst) => (
              <div
                key={inst.id}
                className="flex gap-4 p-4 rounded-xl border border-slate-200 bg-white"
              >
                <div className="w-16 h-16 rounded-full bg-slate-200 overflow-hidden flex-shrink-0">
                  {getPublicImageUrl(inst.profile_image_url, 'profile-images') ? (
                    <img
                      src={getPublicImageUrl(inst.profile_image_url, 'profile-images')}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <SafeIcon icon={FiUser} className="w-8 h-8 text-slate-400" />
                    </div>
                  )}
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{inst.full_name}</p>
                  {inst.title && <p className="text-sm text-slate-500">{inst.title}</p>}
                  {inst.bio && <p className="text-sm text-slate-600 mt-1">{inst.bio}</p>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Learning progress (resources) */}
      <section>
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Learning progress</h2>
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <ResourceProgressTracker
            resources={data.resources ?? []}
            resourceProgress={data.resourceProgress ?? {}}
            progressPercentage={data.progressPercentage ?? 0}
            completedCount={data.completedResources}
            totalCount={data.totalResources}
            onToggleComplete={handleMarkResourceComplete}
            loading={progressLoading}
          />
        </div>
      </section>

      {/* Homework */}
      {homeworkWithSubmissions.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Homework & assignments</h2>
          <div className="space-y-4">
            {homeworkWithSubmissions.map((hw) => (
              <HomeworkCard
                key={hw.id}
                homework={hw}
                submission={hw.submission}
                onSubmit={(payload) => handleSubmitHomework(hw.id, payload)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Registration info */}
      {(registration || agentProfile) && (
        <section>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Registration information</h2>
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-2 text-sm">
            {agentProfile && (
              <p>
                <span className="text-slate-500">Referred by:</span>{' '}
                {agentProfile.full_name}
                {agentProfile.email && ` · ${agentProfile.email}`}
                {agentProfile.phone && ` · ${agentProfile.phone}`}
              </p>
            )}
            {registration && (
              <p>
                <span className="text-slate-500">Registration:</span>{' '}
                {registration.created_at
                  ? new Date(registration.created_at).toLocaleDateString()
                  : '—'}{' '}
                · Payment: {registration.payment_status ?? '—'}
              </p>
            )}
          </div>
        </section>
      )}

      {/* Resources (downloads) - already in progress tracker; optional duplicate list */}
      {data.resources?.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Resources</h2>
          <ul className="space-y-2">
            {data.resources.map((r) => (
              <li key={r.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <span className="font-medium text-slate-900">{r.title}</span>
                {r.file_url && (
                  <a
                    href={r.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#00AAFF] text-sm inline-flex items-center gap-1"
                  >
                    {r.resource_type === 'link' ? (
                      <><SafeIcon icon={FiExternalLink} className="w-4 h-4" /> Open</>
                    ) : (
                      <><SafeIcon icon={FiDownload} className="w-4 h-4" /> Download</>
                    )}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Learning objectives */}
      {data.learningObjectives?.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Learning objectives</h2>
          <ul className="list-disc list-inside space-y-1 text-slate-700">
            {data.learningObjectives.map((obj) => (
              <li key={obj.id}>
                {obj.objective_text || obj.title || obj.description}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
